import React, { type FunctionComponent, useState } from 'react'
import {
  Alert,
  Button,
  Modal,
  ModalBody,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  NewSelect,
  Text,
} from '@retailer-platform/shared-components'
import { startCase } from 'lodash'
import {
  QualityAuditColumnConfigInputSelectionType,
  type QualityAuditWorkflowColumnConfigInput,
} from '../../../types/qualityAuditWorkflows.types.ts'
import { Input } from '../../md-platform-list/components/MdPlatformModifyModal.tsx'
import { useDomainMessages } from '../../../utils/domain/intl.ts'
import { PropertyContainer } from '../../shared-styled-components/SharedStyledComponents.tsx'
import {
  Divider,
  Property,
  PropertySection,
  PropertyTitle,
  TextInput,
} from '../../../utils/common.styles.tsx'

interface Props {
  inputConfig: QualityAuditWorkflowColumnConfigInput
  onClose: () => void
  onSave: (inputConfig: QualityAuditWorkflowColumnConfigInput) => void
}

interface State {
  type: string | undefined
}

export const QualityAuditWorkflowConfigColumnInputModal: FunctionComponent<
  React.PropsWithChildren<Props>
> = ({ onClose, onSave, inputConfig }) => {
  const [inputConfigState, setInputConfigState] =
    useState<QualityAuditWorkflowColumnConfigInput>(inputConfig)
  const inputType = inputConfig?.group_name
    ? QualityAuditColumnConfigInputSelectionType.group
    : inputConfigState?.type
    ? QualityAuditColumnConfigInputSelectionType.single
    : undefined
  const [state, setState] = useState<State>({
    type: inputType,
  })

  const i18n = useDomainMessages({
    createTitle:
      'catalogAdminDomain.qualityAuditWorkflows.configs.columns.input.modal.title.create',
    changeTitle:
      'catalogAdminDomain.qualityAuditWorkflows.configs.columns.input.modal.title.change',
    info: 'catalogAdminDomain.qualityAuditWorkflows.configs.columns.input.modal.info',
    typeLabel: 'catalogAdminDomain.qualityAuditWorkflows.configs.columns.input.modal.type.label',
    typeDetails:
      'catalogAdminDomain.qualityAuditWorkflows.configs.columns.input.modal.type.details',
    nameLabel: 'catalogAdminDomain.qualityAuditWorkflows.configs.columns.input.modal.name.label',
    nameDetails:
      'catalogAdminDomain.qualityAuditWorkflows.configs.columns.input.modal.name.details',
    or: 'catalogAdminDomain.qualityAuditWorkflows.configs.columns.input.modal.or.label',
    groupNameLabel:
      'catalogAdminDomain.qualityAuditWorkflows.configs.columns.input.modal.groupName.label',
    groupNameDetails:
      'catalogAdminDomain.qualityAuditWorkflows.configs.columns.input.modal.groupName.details',
    save: 'catalogAdminDomain.qualityAuditWorkflows.configs.modal.save',
    close: 'catalogAdminDomain.qualityAuditWorkflows.configs.modal.close',
    selection: 'catalogAdminDomain.qualityAuditWorkflows.configs.modal.selection',
    selectionDetails: 'catalogAdminDomain.qualityAuditWorkflows.configs.modal.selection.details',
    features: 'catalogAdminDomain.qualityAuditWorkflows.configs.modal.features',
    featureDetails: 'catalogAdminDomain.qualityAuditWorkflows.configs.modal.features.details',
  })

  const isIndividualInput =
    (inputConfigState?.name ?? '').length > 0 && (inputConfigState?.type ?? '').length > 0
  const isGroupInput = (inputConfigState?.group_name ?? '').length > 0
  const isDisabled = !isIndividualInput && !isGroupInput
  const isExisting =
    (inputConfig?.name ?? '').length > 0 ||
    (inputConfig?.type ?? '').length > 0 ||
    (inputConfig?.group_name ?? '').length > 0

  const onChangeName = (value: string) => {
    setInputConfigState({
      ...inputConfigState,
      name: value,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      group_name: undefined,
    })
  }

  const onChangeType = (value: string) => {
    setInputConfigState({
      ...inputConfigState,
      type: value,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      group_name: undefined,
    })
  }

  const onChangeGroupName = (value: string) => {
    setInputConfigState({
      ...inputConfigState,
      name: undefined,
      type: undefined,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      group_name: value,
    })
  }

  const onChangeFeature = (value: string) => {
    setInputConfigState({
      ...inputConfigState,
      features: value,
    })
  }

  return (
    <Modal maxWidth={'50%'} maxHeight={'100%'} isOpen={true} hideCloseButton={true}>
      <ModalContainer>
        <ModalHeader>{isExisting ? i18n.changeTitle : i18n.createTitle}</ModalHeader>
        <ModalBody>
          <PropertyContainer style={{ flexDirection: 'column' }}>
            <Property style={{ flex: 1 }}>
              <PropertyTitle>{i18n.selection}</PropertyTitle>
              <Text size="small" css={{ flex: 1 }}>
                {i18n.selectionDetails}
              </Text>
              <NewSelect
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: base => ({
                    ...base,
                    zIndex: 9000,
                  }),
                }}
                value={(state.type ?? '') as QualityAuditColumnConfigInputSelectionType}
                options={[
                  {
                    label: startCase(QualityAuditColumnConfigInputSelectionType.single),
                    value: QualityAuditColumnConfigInputSelectionType.single,
                  },
                  {
                    label: startCase(QualityAuditColumnConfigInputSelectionType.group),
                    value: QualityAuditColumnConfigInputSelectionType.group,
                  },
                ]}
                isSearchable={true}
                onChange={value => {
                  setState({
                    ...state,
                    type: value,
                  })
                }}
              />
              <Alert variant={'info'}>{i18n.info}</Alert>
            </Property>
            {state.type === QualityAuditColumnConfigInputSelectionType.single && (
              <PropertySection style={{ flexDirection: 'row' }}>
                <Property style={{ flex: 1 }}>
                  <PropertyTitle>{i18n.typeLabel}</PropertyTitle>
                  <Text size="small" css={{ flex: 1 }}>
                    {i18n.typeDetails}
                  </Text>
                  <Input
                    style={{ width: '100%' }}
                    error={false}
                    onChange={event => onChangeType(event.target.value)}
                    value={inputConfigState?.type ?? ''}
                  />
                </Property>
                <Property style={{ flex: 1 }}>
                  <PropertyTitle>{i18n.nameLabel}</PropertyTitle>
                  <Text size="small" css={{ flex: 1 }}>
                    {i18n.nameDetails}
                  </Text>
                  <Input
                    style={{ width: '100%' }}
                    error={false}
                    onChange={event => onChangeName(event.target.value)}
                    value={inputConfigState?.name ?? ''}
                  />
                </Property>
              </PropertySection>
            )}
            {state.type === QualityAuditColumnConfigInputSelectionType.group && (
              <PropertySection style={{ flexDirection: 'row' }}>
                <Property style={{ flex: 1 }}>
                  <PropertyTitle>{i18n.groupNameLabel}</PropertyTitle>
                  <Text size="small" css={{ flex: 1 }}>
                    {i18n.groupNameDetails}
                  </Text>
                  <Input
                    style={{ width: '100%' }}
                    error={false}
                    onChange={event => onChangeGroupName(event.target.value)}
                    value={inputConfigState?.group_name ?? ''}
                  />
                </Property>
              </PropertySection>
            )}
            <PropertySection style={{ flexDirection: 'row' }}>
              <Property style={{ flex: 1 }}>
                <Divider />
                <PropertyTitle>{i18n.features}</PropertyTitle>
                <Text size="small" css={{ flex: 1 }}>
                  {i18n.featureDetails}
                </Text>
                <TextInput
                  style={{ width: '100%', height: '200px' }}
                  error={false}
                  onChange={event => onChangeFeature(event.target.value)}
                  value={inputConfigState?.features ?? ''}
                />
              </Property>
            </PropertySection>
          </PropertyContainer>
        </ModalBody>
        <ModalFooter>
          <Button variant={'secondary'} onClick={() => onClose()}>
            {i18n.close}
          </Button>
          <Button
            variant={'primary'}
            disabled={isDisabled}
            onClick={() => onSave(inputConfigState)}
          >
            {i18n.save}
          </Button>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  )
}
